import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { X, Search, RefreshCcwIcon, CheckIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import { listKnowledgeBases, listKnowledgeBasesPaginated } from "../../../../service/apiService";
import BrainBankFileUploader from "./BrainBankFileUploader";
import CreateBrainBankModal from "./CreateBrainBankModal";
import { useUser } from "../../../../context/UserContext";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";

interface KnowledgeBase {
  id: string;
  name: string;
  knowledgebase_name?: string; 
  index_name?: string;
}

interface BrainBankSelectorProps {
  selectedKnowledgeBaseIds: string[];
  onKnowledgeBaseSelected: (id: string) => void;
  onKnowledgeBaseRemoved: (id: string) => void;
  isEnabled: boolean;
}

const BrainBankSelector: React.FC<BrainBankSelectorProps> = ({ 
  selectedKnowledgeBaseIds, 
  onKnowledgeBaseSelected, 
  onKnowledgeBaseRemoved,
  isEnabled
}) => {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [isLoadingBrainBanks, setIsLoadingBrainBanks] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';
  const { canCreateBrainBank } = useCreatePermissions();

  const fetchKnowledgeBases = async () => {
  setIsLoadingBrainBanks(true);
  try {
    console.log('BrainBankSelector: Fetching knowledge bases');
    console.log('User ID:', userId, 'Is Admin:', isAdmin);
    
    let result;
    if (isAdmin) {
      // Admin sees all BrainBanks
      console.log('Admin fetching all BrainBanks');
      result = await listKnowledgeBases();
      console.log('Admin result:', result);
    } else {
      // Regular users see their own + assigned BrainBanks
      console.log('User fetching paginated BrainBanks');
      
      // Get both "my" and "assigned" BrainBanks
      const [myResult, assignedResult] = await Promise.all([
        listKnowledgeBasesPaginated(1, 100, 'my_knowledge_base'),
        listKnowledgeBasesPaginated(1, 100, 'assigned_knowledge_base')
      ]);
      
      console.log('My BrainBanks:', myResult);
      console.log('Assigned BrainBanks:', assignedResult);
      
      // Combine both results and remove duplicates
      const myBrainBanks = myResult?.knowledge_bases || [];
      const assignedBrainBanks = assignedResult?.knowledge_bases || [];
      
      // Create a Map to avoid duplicates
      const brainBankMap = new Map();
      
      [...myBrainBanks, ...assignedBrainBanks].forEach(kb => {
        brainBankMap.set(kb.id, kb);
      });
      
      result = Array.from(brainBankMap.values());
    }
    
    const mappedResults = result.map((kb: any) => ({
      id: kb.id,
      name: kb.knowledgebase_name || kb.name,
      index_name: kb.index_name
    }));
    
    console.log('Final mapped results:', mappedResults);
    setKnowledgeBases(mappedResults);
  } catch (error) {
    console.error("Error fetching BrainBanks:", error);
    toast.error("Failed to load BrainBanks");
  } finally {
    setIsLoadingBrainBanks(false);
  }
};

useEffect(() => {
  if (userId) {
    fetchKnowledgeBases();
  }
}, [userId, isAdmin]);

  const filteredKnowledgeBases = useMemo(() => {
    return searchQuery === ""
      ? knowledgeBases
      : knowledgeBases.filter(kb =>
          kb.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
  }, [knowledgeBases, searchQuery]);

  // Only show knowledge bases that are not already selected
  const availableKnowledgeBases = useMemo(() => {
    return filteredKnowledgeBases.filter(
      kb => !selectedKnowledgeBaseIds.includes(kb.id)
    );
  }, [filteredKnowledgeBases, selectedKnowledgeBaseIds]);

  // Get the selected knowledge bases data
  const selectedKnowledgeBases = useMemo(() => {
    return selectedKnowledgeBaseIds.map(id => {
      const kb = knowledgeBases.find(b => b.id === id);
      return {
        id,
        name: kb ? kb.name : "Loading...",
        index_name: kb?.index_name
      };
    });
  }, [selectedKnowledgeBaseIds, knowledgeBases]);

  // Function to get existing file names (to prevent duplicates)
  const getExistingFileNames = (knowledgeBaseId: string): string[] => {
    return [];
  };

  // Handle the creation of a new BrainBank
  const handleCreateBrainBankSuccess = (newBrainBankId: string, newBrainBankName: string) => {
    // Add the new BrainBank to the list
    setKnowledgeBases(prev => [
      ...prev, 
      { 
        id: newBrainBankId, 
        name: newBrainBankName,
        index_name: ''  // We might not have this information, but it's not critical for display
      }
    ]);

    // Close the modal
    setShowCreateModal(false);
    
    // Refresh the list to ensure we have all data
    fetchKnowledgeBases();
  };

  // Open the create BrainBank modal
  const handleOpenCreateModal = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default form submission
    e.stopPropagation(); // Stop event propagation
    if (!isEnabled) return;
    setShowCreateModal(true);
  };

  return (
    <div className={!isEnabled ? 'opacity-50 pointer-events-none' : ''}>
      {/* Search Input and Create Button */}
      <div className="relative mb-3 flex">
        <div className="flex-grow relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pl-9 pr-3 py-1.5 border border-gray-200 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
            placeholder="Search BrainBanks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {isLoadingBrainBanks && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400" />
            </div>
          )}
        </div>
        
        {/* Create BrainBank button */}
        {canCreateBrainBank && (
          <button
            type="button"
            onClick={handleOpenCreateModal}
            className="ml-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm flex items-center"
            disabled={!isEnabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create
          </button>
        )}
      </div>
      
      {/* Display for selected BrainBanks with "Add File" action */}
      {selectedKnowledgeBaseIds.length > 0 && (
  <div className="space-y-1 mb-3 pb-3 border-b border-gray-100">
    <div className="text-xs font-medium text-gray-500 mb-1">Associated BrainBanks:</div>
    {isLoadingBrainBanks ? (
      <div className="flex items-center justify-center py-2">
        <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Loading associated BrainBanks...</span>
      </div>
    ) : selectedKnowledgeBases.length === 0 ? (
      <div className="text-sm text-gray-500 py-2">
        No associated BrainBanks found
      </div>
    ) : (
      selectedKnowledgeBases.map(kb => (
            <div key={kb.id} className="flex items-center justify-between text-sm py-1">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                <span>{kb.name}</span>
              </div>
              <BrainBankFileUploader 
                knowledgeBaseId={kb.id} 
                brainBankName={kb.name}
                indexName={kb.index_name || kb.id} 
                existingFiles={getExistingFileNames(kb.id)}
                onFileUploaded={() => {
                  toast.success(`File uploaded to ${kb.name} successfully`);
                }}
              />
            </div>
      ))
    )}
  </div>
)}
      
      {/* Selected BrainBanks chips */}
      {selectedKnowledgeBases.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedKnowledgeBases.map(kb => (
            <div 
              key={`selected-${kb.id}`}
              className="flex items-center bg-violet-50 text-violet-700 text-xs rounded-full px-2 py-1"
            >
              <span className="mr-1">{kb.name}</span>
              <button
                type="button"
                onClick={() => onKnowledgeBaseRemoved(kb.id)}
                className="text-violet-500 hover:text-violet-700 focus:outline-none"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Available BrainBanks list */}
      <div className="max-h-28 overflow-y-auto">
        {isLoadingBrainBanks ? (
          <div className="flex justify-center py-4">
            <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
          </div>
        ) : availableKnowledgeBases.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-2">
            {searchQuery ? "No BrainBanks found" : "No available BrainBanks"}
          </div>
        ) : (
          <div className="space-y-1">
            {availableKnowledgeBases.map(kb => {
              return (
                <div 
                  key={kb.id}
                  className="flex items-center justify-between rounded hover:bg-gray-50 cursor-pointer px-2 py-1.5"
                  onClick={() => onKnowledgeBaseSelected(kb.id)}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`kb-${kb.id}`}
                      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      onChange={() => {}}
                      checked={false}
                    />
                    <label 
                      htmlFor={`kb-${kb.id}`}
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      {kb.name}
                    </label>
                  </div>
                  <CheckIcon className="h-4 w-4 text-violet-600 opacity-0 group-hover:opacity-100" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create BrainBank Modal */}
      <Transition appear show={showCreateModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => setShowCreateModal(false)}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
            
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-3xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <CreateBrainBankModal 
                  onClose={() => setShowCreateModal(false)}
                  onSuccess={handleCreateBrainBankSuccess}
                />
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default BrainBankSelector;